import {ToggleButton, ToggleButtonGroup, TextField, Button, Box, Typography, Container, Divider} from "@mui/material";
import React from "react";
import * as PropTypes from "prop-types";
import {MasterDataService} from "../../services/MasterDataService";


const textFieldStyle = {margin: "5px"};
const searchPageCategoryOrder = ["Massage", "Beauty", "Fitness", "Nutrition", "Tutoring", "Wealth", "Kids", "Children"];

const sortCategoriesLikeSearchPage = (categories) => {
    return [...(categories || [])].sort((a, b) => {
        const aIndex = searchPageCategoryOrder.indexOf(a);
        const bIndex = searchPageCategoryOrder.indexOf(b);

        if (aIndex === -1 && bIndex === -1) {
            return a.localeCompare(b);
        }
        if (aIndex === -1) {
            return 1;
        }
        if (bIndex === -1) {
            return -1;
        }
        return aIndex - bIndex;
    });
};

const sortCategoriesWithConfigs = (categories, configs) => {
    if (!Array.isArray(configs) || configs.length === 0) {
        return sortCategoriesLikeSearchPage(categories);
    }

    // Build a map of configured categories (with their order values)
    const configMap = {};
    configs.forEach((config) => {
        if (config && config.key) {
            configMap[config.key] = config.order || 0;
        }
    });

    return [...(categories || [])].sort((a, b) => {
        const aOrder = configMap[a];
        const bOrder = configMap[b];

        // Both have config entries
        if (aOrder !== undefined && bOrder !== undefined) {
            // If both are positioned (order > 0), sort by order
            if (aOrder > 0 && bOrder > 0) {
                return aOrder - bOrder;
            }
            // If only a is positioned, a comes first
            if (aOrder > 0) {
                return -1;
            }
            // If only b is positioned, b comes first
            if (bOrder > 0) {
                return 1;
            }
            // Both are auto (order === 0), sort alphabetically
            return a.localeCompare(b);
        }

        // a is configured, b is not: a comes first
        if (aOrder !== undefined) {
            return -1;
        }

        // b is configured, a is not: b comes first
        if (bOrder !== undefined) {
            return 1;
        }

        // Neither configured: alphabetical
        return a.localeCompare(b);
    });
};


export class ServiceEditorForm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            services: [],
            categories: [],
        }
    }

    componentDidMount() {
        Promise.all([
            MasterDataService.getAllCategories(),
            MasterDataService.getCategoryConfigs(),
        ]).then(([categories, configs]) => {
            const hidden = new Set((configs || []).filter((c) => c && c.hidden).map((c) => c.key));
            const visible = (categories || []).filter((name) => !hidden.has(name));
            this.setState({categories: sortCategoriesWithConfigs(visible, configs)});
        });
    }

    componentDidUpdate(prevProps) {
        // When the selected service changes and the new one has no category
        // (i.e. "Add New" was clicked), clear the available-services list so
        // the previous category's options don't bleed into the new entry.
        if (
            this.props.selectedServiceIndex !== prevProps.selectedServiceIndex &&
            !this.props.provider?.servicesProvided[this.props.selectedServiceIndex]?.category
        ) {
            this.setState({ services: [] });
        }
    }

    handleSelectCategory(e, category) {
        if (!category) {
            return;
        }
        MasterDataService.getListOfServices(category).then((response) => this.setState({services: response}));

        let provider = {...this.props.provider};
        provider.servicesProvided[this.props.selectedServiceIndex].category = category;
        this.props.setProvider(provider);
    }

    handleSelectService(e, service) {
        if (!service) {
            return;
        }
        let provider = {...this.props.provider};
        provider.servicesProvided[this.props.selectedServiceIndex].name = service;
        this.props.setProvider(provider);
    }

    handleDescriptionChanged(e) {
        let provider = {...this.props.provider};
        provider.servicesProvided[this.props.selectedServiceIndex].description = e.target.value;
        this.props.setProvider(provider);
    }

    onAddTimeClick(e) {
        let provider = {...this.props.provider};
        provider.servicesProvided[this.props.selectedServiceIndex].offeredLengths.push({length: 30, price: 59.99});
        this.props.setProvider(provider);
    }

    render() {
        const provider = this.props.provider;

        var listOfCategories = this.state.categories?.map((category) =>
            <ToggleButton key={category} value={category}>{category}</ToggleButton>
        );

        var listOfExistingServices = provider.servicesProvided.map((service) => service.name);
        if (this.props.addNew && listOfExistingServices.length > 0) {
            listOfExistingServices = listOfExistingServices.slice(0, listOfExistingServices.length - 1);
        }
        var listOfServices = this.state.services?.filter((service) => !listOfExistingServices.includes(service)).map((service) =>
            <ToggleButton key={service} value={service}>{service}</ToggleButton>);

        return <>
            <Container sx={{background: "white"}}>
                <Typography variant={"subtitle2"}>Category</Typography>

                <Container>
                    {this.props.addNew ?

                        <ToggleButtonGroup
                            color={"primary"}
                            id="serviceSelector"
                            value={provider.servicesProvided[this.props.selectedServiceIndex]?.category}
                            exclusive
                            onChange={this.handleSelectCategory.bind(this)}
                        >
                            {listOfCategories}
                        </ToggleButtonGroup>

                        : <Button variant={'contained'}
                                  fullWidth={true}>{provider.servicesProvided[this.props.selectedServiceIndex]?.category}</Button>

                    }</Container>
            </Container>
            <Divider sx={{margin: '10px 0', borderBottomWidth: '2px'}}/>
            <Container>
                <Typography variant={"subtitle2"}>Services</Typography>
                <Container>
                    {this.props.addNew ?
                        <ToggleButtonGroup
                            color={"primary"}
                            value={provider.servicesProvided[this.props.selectedServiceIndex]?.name}
                            id="serviceSelector"
                            fullWidth={true}
                            exclusive
                            onChange={this.handleSelectService.bind(this)}
                        >
                            {listOfServices}
                        </ToggleButtonGroup>
                        : <Button variant={'contained'}
                                  fullWidth={true}>{provider.servicesProvided[this.props.selectedServiceIndex]?.name}</Button>
                    }
                </Container>

            </Container>
            <Divider sx={{margin: '10px 0', borderBottomWidth: '2px'}}/>
            <Container>
                <Typography variant={"subtitle2"}>Offered Lengths</Typography>
                <Container>
                    {(provider.servicesProvided[this.props.selectedServiceIndex]?.name !== "") && provider.servicesProvided[this.props.selectedServiceIndex]?.offeredLengths.map((length, index) => (

                        <Box sx={{
                            gridTemplateColumns: 'repeat(2, minmax(150px, 1fr))',
                            display: 'grid',
                            gap: '10px',
                            marginBottom: '10px',
                            marginTop: '10px'
                        }}>
                            <TextField variant="outlined"
                                       type="number"
                                       key={"length." + index}
                                       value={length.length}
                                       label="Length"
                                       onChange={
                                           (e) => {
                                               let provider = {...this.props.provider};
                                               provider.servicesProvided[this.props.selectedServiceIndex].offeredLengths[index].length = Number.parseInt(e.target.value);
                                               this.props.setProvider(provider);
                                           }
                                       }/>
                            <TextField variant="outlined"
                                       value={length.price}
                                       key={"price." + index}
                                       type="number"
                                       label="Price"
                                       onChange={
                                           (e) => {
                                               let provider = {...this.props.provider};
                                               provider.servicesProvided[this.props.selectedServiceIndex].offeredLengths[index].price = Number.parseFloat(e.target.value);
                                               this.props.setProvider(provider);
                                           }
                                       }/>
                        </Box>
                    ))}
                    <Button
                        variant="outlined"
                        onClick={this.onAddTimeClick.bind(this)}
                        fullWidth
                    >
                        Add Time
                    </Button>

                </Container>
            </Container>
            <Divider sx={{margin: '10px 0'}}/>
            <Container>
                <Typography variant={"subtitle2"}>Description</Typography>
                <TextField
                    fullWidth={true}
                    minRows="4"
                    multiline={true}
                    value={provider.servicesProvided[this.props.selectedServiceIndex]?.description || ""}
                    onChange={this.handleDescriptionChanged.bind(this)}
                />

            </Container>
        </>;
    }
}

const servicePropType = PropTypes.shape({
    category: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    offeredLengths: PropTypes.arrayOf(
        PropTypes.shape({
            length: PropTypes.number.isRequired,
            price: PropTypes.number.isRequired,
        })
    ),
    description: PropTypes.string.isRequired,
});

const providerPropType = PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    postalCode: PropTypes.string.isRequired,
    categoryType: PropTypes.string.isRequired,
    servicesProvided: PropTypes.arrayOf(servicePropType).isRequired,
    phoneNumber: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    thumbnail: PropTypes.string.isRequired,
    thumbnailUrl: PropTypes.string.isRequired,
    rating: PropTypes.number.isRequired,
    businessSummary: PropTypes.string.isRequired,
    carouselImages: PropTypes.arrayOf(PropTypes.string).isRequired,
    imageList: PropTypes.arrayOf(PropTypes.string).isRequired,
    etag: PropTypes.string.isRequired,
});

ServiceEditorForm.propTypes = {
    provider: providerPropType.isRequired,
    setProvider: PropTypes.func.isRequired,
    selectedServiceIndex: PropTypes.number.isRequired,
    addNew: PropTypes.bool,
};
